import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchFacets } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import DrawerViewGuidanceReport, { emptyPayload } from '@app/src/components/Drawer/Views/DrawerViewGuidanceReport'
import DrawerViewBulkResponseItemExternalVerification from '@app/src/components/Drawer/Views/Verification/DrawerViewBulkResponseItemExternalVerification'
import DrawerViewBulkResponseItemInternalVerification from '@app/src/components/Drawer/Views/Verification/DrawerViewBulkResponseItemInternalVerification'
import { useDrawer_DEPRECATED } from '@app/src/components/Drawer_DEPRECATED'
import UpdateProvidersDrawer from '@app/src/components/ManageProviderDrawer/UpdateProvidersDrawer'
import Table from '@app/src/components/Table'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import PreviousNumberAnswersContextProvider from '@app/src/context/PreviousNumberAnswersContextProvider'
import { getResponseItemExportColumnsAccessor } from '@app/src/export-columns/responseItem'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import useSort from '@app/src/hooks/sorting'
import useProviderType from '@app/src/hooks/useProviderType'
import { useSelectableRowsWithFacetsSelectAll } from '@app/src/hooks/useSelectableRows/useSelectableRowsWithFacetsSelectAll'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import useSelectedProviderData from '@app/src/pages/ResourceCollection/Collections/DataHub/AllAnswers/useSelectedProvidersData'
import DataHubFiltersAllAnswers from '@app/src/pages/ResourceCollection/Filters/DataHubFiltersAllAnswers'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import {
  Operators,
  RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
  RESPONSE_ITEM_NOT_SCRAPED_FILTER,
} from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { ExportTemplate } from '@app/src/types/export'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { ResourceTypes } from '@app/src/wf-constants'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ResponseItemCompanyHeader from './ResponseItemCompanyHeader'
import ResponseItemCompanyRow from './ResponseItemCompanyRow'
import ResponseItemHeader from './ResponseItemHeader'
import ResponseItemRow from './ResponseItemRow'
import useSelectedAnswersData from './useSelectedAnswersData'

interface DataHubAnswersTableProps {
  relatedContentFilter?: FilterGroup[]
  allowedFilters: string[]
  userFilters: FilterGroup[]
  tabs?: ResourceCollectionSceneProps['tabs']
  embedded?: ResourceCollectionSceneProps['embedded']
  showCompanyCell?: boolean
}

const DataHubAnswersTable: React.FC<DataHubAnswersTableProps> = ({
  relatedContentFilter = [],
  allowedFilters,
  userFilters,
  tabs,
  embedded,
  showCompanyCell,
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery(undefined, [userFilters])
  const { openDrawer } = useDrawer()
  const { solution } = useAuthentication().scope
  const { productMapping, showAdditionalExportColumns } = useFlags()
  const responseItemExportSchema = getResponseItemExportColumnsAccessor(
    solution,
    productMapping,
    showAdditionalExportColumns,
  )
  const providerType = useProviderType()
  const [isUpdateDrawerOpen, openUpdateDrawer, closeUpdateDrawer] = useDrawer_DEPRECATED(false)
  const hasWriteAccess = useWriteAccess()
  const [onlyLatest, setOnlyLatest] = useState(true)
  const [allowGuidanceReport, setAllowGuidanceReport] = useState(false)

  const basePayload = {
    filter: relatedContentFilter,
    sort: sorting,
    include: [
      'requestItem',
      'response.request',
      'creatorOrganization',
      'response.request.creatorOrganization',
      'response.request.responderOrganization',
      'response.request.subscriptions.target.referralContact',
      'response.request.subscriptions.target.country.name',
      ...(productMapping ? ['response.request.subscriptions.targetProduct'] : []),
      'requestItem.unit',
      'requestItem.questionType',
      'requestItem.section',
      'coordinates',
      'files',
      'flagRule.suggestedRiskGuidance',
      'response.request.target.organization',
      'verifications',
    ],
  }

  const rawExportPayload = {
    ...basePayload,
    include: [
      ...basePayload.include,
      'response.request.target',
      'response.request.subscriptions.target.categoryOptions',
      'response.verifications',
      'response.request.subscriptions.target.country.name',
      'scrapedAnswer.citations',
      'response.request.subscriptions.target.organization.contacts.user',
      'coordinates',
    ],
  }

  const payload = {
    ...basePayload,
    filter: [...basePayload.filter, ...userFilters, ...insertIf(onlyLatest, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER)],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const {
    items: pageResponseItems,
    count,
    isLoading,
    isFetching,
    isError,
  } = useFetchCollectionWithPost<ResponseItem>({
    key: FetchKey.Answer,
    endpoint: endpoints.responseItemsCollection,
    payload,
  })

  const {
    selectedRowsIds,
    isLoadingAllRows,
    isAllSelected,
    handleSelectAll,
    handleCheckboxChange,
    isHeaderChecked,
    handleHeaderCheckboxChange,
  } = useSelectableRowsWithFacetsSelectAll<ResponseItem>({
    rowsInPage: pageResponseItems,
    fetchKey: FetchKey.ResponseItemsFacets,
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: 'id',
    filters: payload.filter,
  })

  const { matchingProviderCount, fetchProviders, providers, isLoadingProviderData } = useSelectedProviderData({
    selectedRowsIds: selectedRowsIds,
  })

  const {
    facets: [nonScrapedSelectedIds],
    isLoading: isLoadingNonScrapedSelectedIds,
  } = useFetchFacets({
    key: FetchKey.ResponseItemsFacets,
    endpoint: endpoints.responseItemsWithFacets,
    options: { enabled: selectedRowsIds?.length > 0 },
    facetsParam: [{ name: 'id' }],
    filter: [
      ...basePayload.filter,
      ...userFilters,
      RESPONSE_ITEM_NOT_SCRAPED_FILTER,
      ...insertIf(onlyLatest, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER),
      { name: 'id', filters: [{ value: selectedRowsIds, operator: Operators.In }] },
    ],
  })

  const {
    fetchExternalVerificationData,
    isLoadingExternalVerificationData,
    fetchInternalVerificationData,
    isLoadingInternalVerificationData,
  } = useSelectedAnswersData({
    filters: payload.filter,
    selectedRowsIds: selectedRowsIds,
  })

  // Only after we've fetched items we call exportTemplateCollection to determine the value for allowGuidanceReport
  const { data: exportTemplates } = useFetchCollectionWithPost<ExportTemplate>({
    key: FetchKey.ExportTemplate,
    endpoint: endpoints.exportTemplateCollection,
    payload: emptyPayload,
  })

  useEffect(() => {
    if (exportTemplates?.items.length) {
      setAllowGuidanceReport(true)
    }
  }, [exportTemplates])

  const categorizeSuppliers = async () => {
    await fetchProviders()
    openUpdateDrawer()
  }

  const nonScrapedItemIds = selectedRowsIds.filter(id => nonScrapedSelectedIds?.some(item => item.value === id))

  const showTargetProductColumn = useMemo(
    () => productMapping && pageResponseItems.some(item => item.response.request.targetProduct),
    [pageResponseItems, productMapping],
  )

  // Extract base props for better maintainability
  const baseRowProps = { showTargetProductColumn }
  const rowComponentProps = showCompanyCell
    ? {
        ...baseRowProps,
        onCheckboxChange: handleCheckboxChange,
        selectedRowsIds: selectedRowsIds,
      }
    : {
        ...baseRowProps,
        showAnswerClassificationResults: true,
      }

  const baseHeaderProps = {
    toggleSorting,
    activeSorting: sorting,
    showTargetProductColumn,
  }

  const handleExport = (selectedIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.Answer}
        count={count}
        userFilter={userFilters}
        exportColumns={responseItemExportSchema}
        rawExportPayload={rawExportPayload}
        onlyLatest={onlyLatest}
        selectedIds={selectedIds}
      />,
    )
  }

  return (
    <PreviousNumberAnswersContextProvider responseItems={pageResponseItems}>
      <ResourceCollectionScene
        title={formatMessage({ id: 'schemas.responseItem.responseItems' })}
        actionButtons={[
          ...insertIf<ActionButton>(allowGuidanceReport, {
            label: formatMessage({ id: 'resourceCollections.general.guidanceReport' }),
            variant: 'outlined',
            onClick: () =>
              openDrawer(
                <DrawerViewGuidanceReport
                  resourceType={ResourceTypes.ResponseItem}
                  count={count}
                  rawExportPayload={rawExportPayload}
                  userFilter={userFilters}
                  exportColumns={responseItemExportSchema}
                  onlyLatest={onlyLatest}
                />,
              ),
            disabled: isLoading || count === 0,
          }),
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () => handleExport(selectedRowsIds),
            disabled: isLoading || count === 0,
          },
        ]}
        buttonRow={
          selectedRowsIds.length
            ? [
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage({ id: 'dataHub.verification.review' }, { count: nonScrapedItemIds.length }),
                  onClick: async () => {
                    await fetchExternalVerificationData()
                    openDrawer(
                      <DrawerViewBulkResponseItemExternalVerification
                        itemIds={nonScrapedItemIds}
                        filters={payload.filter}
                      />,
                    )
                  },
                  variant: 'contained',
                  loading: isLoadingExternalVerificationData,
                  disabled:
                    !nonScrapedItemIds.length ||
                    isLoading ||
                    isFetching ||
                    isLoadingAllRows ||
                    isLoadingNonScrapedSelectedIds,
                }),
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: 'dataHub.verification.internalReview' },
                    { count: nonScrapedItemIds.length },
                  ),
                  onClick: async () => {
                    await fetchInternalVerificationData()
                    openDrawer(
                      <DrawerViewBulkResponseItemInternalVerification
                        itemIds={nonScrapedItemIds}
                        filters={payload.filter}
                      />,
                    )
                  },
                  variant: 'outlined',
                  loading: isLoadingInternalVerificationData,
                  disabled:
                    !nonScrapedItemIds.length ||
                    isLoading ||
                    isFetching ||
                    isLoadingAllRows ||
                    isLoadingNonScrapedSelectedIds,
                }),
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: `dataHub.categorizeProviders.${providerType.toLowerCase()}` },
                    { count: matchingProviderCount },
                  ),
                  onClick: categorizeSuppliers,
                  variant: 'outlined',
                  loading: isLoadingProviderData,
                  disabled: !selectedRowsIds.length || isLoading || isFetching || isLoadingAllRows,
                }),
                {
                  label: formatMessage(
                    { id: 'resourceCollections.general.exportWithCount' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: () => handleExport(selectedRowsIds),
                  disabled: isLoading || count === 0,
                },
                ...insertIf<ActionButton>(count > pageSize, {
                  label: formatMessage({ id: 'resourceCollections.general.selectAllAnswers' }, { count }),
                  variant: 'text',
                  onClick: handleSelectAll,

                  disabled: isLoading || isFetching || isAllSelected(),
                  loading: isLoadingAllRows,
                }),
              ]
            : undefined
        }
        tabs={tabs}
        filter={
          <DataHubFiltersAllAnswers
            implicitFilters={relatedContentFilter}
            allowedFilters={allowedFilters}
            onlyLatest={onlyLatest}
            setOnlyLatest={setOnlyLatest}
          />
        }
        embedded={embedded}
      >
        <Table<ResponseItem>
          RowComponent={showCompanyCell ? ResponseItemCompanyRow : ResponseItemRow}
          rowComponentProps={rowComponentProps}
          HeaderComponent={() =>
            showCompanyCell ? (
              <ResponseItemCompanyHeader
                {...baseHeaderProps}
                isHeaderChecked={isHeaderChecked}
                handleHeaderCheckboxChange={handleHeaderCheckboxChange}
              />
            ) : (
              <ResponseItemHeader {...baseHeaderProps} />
            )
          }
          data={pageResponseItems}
          isLoading={isLoading || isFetching}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          stickyColumnIndex={1}
        />
      </ResourceCollectionScene>
      <UpdateProvidersDrawer
        selectedProviders={providers.map(provider => ({
          providerId: provider.id,
          name: provider.name,
          activityStatus: provider.activityStatus,
          finalRiskRating: provider.finalRiskRating,
          priority: provider.priority,
          providerApprovalStatus: provider.providerApprovalStatus,
          categoryOptionIds: provider.categoryOptions?.map(catOpt => catOpt.id),
          ownerUserIds: provider.ownerUserIds,
          tier: provider.tier,
          supplierUsage: provider.supplierUsage,
        }))}
        isDrawerOpen={isUpdateDrawerOpen}
        closeDrawer={closeUpdateDrawer}
      />
    </PreviousNumberAnswersContextProvider>
  )
}

export default DataHubAnswersTable
